import { useSelector } from 'react-redux';
import { useCallback } from 'react';
import flowTypes from 'new/components/flows/types/flowTypes';
import { Field, MetaInfo } from '@nexla/@types';

type GetMetaInfoById = (connectorType: string) => MetaInfo;

type GetConfigFields = (
  /**
   * selectedConnection or connectionType
   */
  connection: string,
  flowType?: string,
) => Field[];

type GetSourceConfigFieldsByConnection = (connection: string, filter?: any) => Field[];

type GetAuthFields = (connectorType: string) => any;
type GetConnectionCategoryByConnectorType = (connectorType: string) => string | null;

interface UsePaging {
  paging: any;
  metaInfoById: any;
  commonConnectors: any;
  getAuthFields: GetAuthFields;
  getMetaInfoById: GetMetaInfoById;
  getSourceConfigFields: GetConfigFields;
  getSinkConfigFields: GetConfigFields;
  getConnectionCategoryByCredID: (id: number) => string;
  getSourceConfigFieldsByConnection: GetSourceConfigFieldsByConnection;
  getConnectionCategoryByConnectorType: GetConnectionCategoryByConnectorType;
}

export default function usePaging(): UsePaging {
  const paging = useSelector((state: any) => state.paging);
  const credentials = useSelector((s: any) => s.entities.credentials);
  const metaInfoById = useSelector((state: any) => state.paging.metaInfoById);
  const commonConnectors = useSelector((state: any) => state.paging.commonConnectors);

  const getMetaInfoById: GetMetaInfoById = useCallback(
    (connection: string) => paging.metaInfoById[connection] || {},
    [paging.metaInfoById],
  );

  const getSourceConfigFields: GetConfigFields = useCallback(
    (connectionType, flowType = flowTypes.streaming) => {
      const { fieldsByFlowType } = paging;
      const allConfigFields = paging.allSourceConfigById
        ? connectionType
          ? paging.allSourceConfigById[connectionType]
          : paging.allSourceConfigById[0]
        : null;

      if (!allConfigFields) {
        return [];
      }

      const connSourceFieldsByFlowType = fieldsByFlowType[connectionType];

      if (!flowType || flowType === flowTypes.streaming) {
        return allConfigFields;
      }

      return allConfigFields.filter((field) => {
        // If we don't have a configuration per flow type, all are valid
        if (!connSourceFieldsByFlowType) return true;
        const configByType = connSourceFieldsByFlowType[flowType];
        // If we don't have a configuration for the specific type, all are valid
        if (!configByType?.source) return true;
        return configByType.source.includes(field.name);
      });
    },
    [paging],
  );

  const getSourceConfigFieldsByConnection: GetSourceConfigFieldsByConnection = (
    connection,
    filter,
  ) => {
    if (connection) {
      const { sourceConfigById } = paging;
      const fields = sourceConfigById[connection];
      if (typeof filter === 'function') {
        return fields.filter(filter);
      }
      return fields;
    }
    return [];
  };

  const getSinkConfigFields: GetConfigFields = useCallback(
    (selectedConnection: string, flowType: string = flowTypes.streaming) => {
      const { fieldsByFlowType } = paging;
      const allConfigFields = paging.allSinkConfigById
        ? selectedConnection
          ? paging.allSinkConfigById[selectedConnection]
          : paging.allSinkConfigById[0]
        : null;

      if (!allConfigFields) {
        return [];
      }

      const connFieldsByFlowType = fieldsByFlowType[selectedConnection];

      if (!flowType || flowType === flowTypes.streaming) {
        return allConfigFields;
      }

      return allConfigFields.filter((field) => {
        // If we don't have a configuration per flow type, all are valid
        if (!connFieldsByFlowType) return true;
        const configByType = connFieldsByFlowType[flowType];
        // If we don't have a configuration for the specific type, all are valid
        if (!configByType?.sink) return true;
        return configByType.sink.includes(field.name);
      });
    },
    [paging],
  );

  const getAuthFields: GetAuthFields = useCallback(
    (connectionType: string) => {
      return paging.connAuthById[connectionType] || {};
    },
    [paging],
  );

  const getConnectionCategoryByConnectorType: GetConnectionCategoryByConnectorType = useCallback(
    (connectorType: string) => {
      const meta = metaInfoById[connectorType];
      return meta?.connectionCategory;
    },
    [metaInfoById],
  );

  const getConnectionCategoryByCredID = (id: number) => {
    if (id) {
      const credential = credentials[id];
      const selectedConnection = credential?.connector?.type;
      return getMetaInfoById(selectedConnection)?.connectionCategory;
    }
  };

  return {
    paging,
    metaInfoById,
    commonConnectors,
    getAuthFields,
    getMetaInfoById,
    getSinkConfigFields,
    getSourceConfigFields,
    getConnectionCategoryByCredID,
    getSourceConfigFieldsByConnection,
    getConnectionCategoryByConnectorType,
  };
}
